import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { useStyle } from './Style'
import SkeletonWrapper from '../../../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import useProductCurrency from '../../../../../hooks/useProductCurrency'
import { formatDateLocale, canAccess } from '../../../../../lib/utils.es6'
import ReplayPayment from './Components/ReplayPayment/ReplayPayment'
import ViewDocument from '../../../../../shared-ui/ViewDocument/ViewDocument'
import { getPartnerGuid } from '../../../../../lib/access.es6'
import {
  initiatePremiumTransfer,
  getElasticSearchResults
} from '../../../../../lib/requests.es6'
import {
  approvalErrorMessage,
  transferSubmittedMessage
} from '../../../../../lib/premiumApprovals'

const isAmount = value => typeof value === 'number' && Number.isFinite(value)

// The premium the policy owes for the cover it has been granted. total_payable_
// amount is the engine's figure; the quotation premium is the fallback for a
// policy that has not been priced yet.
export const getPremiumPayable = policy => {
  if (!policy) return null
  if (isAmount(policy.total_payable_amount)) {
    return policy.total_payable_amount
  }
  if (
    policy.quotation &&
    isAmount(policy.quotation.total_premium_amount_in_cents)
  ) {
    return policy.quotation.total_premium_amount_in_cents
  }
  return null
}

// Money received beyond the premium owed, i.e. an amount that cannot be
// allocated to cover. Deliberately NOT derived from balance_amount_in_cents:
// that field is "due now minus paid", so a correctly paid annual policy carries
// a large negative balance that has nothing to do with overpayment.
export const getExcessPaid = policy => {
  if (!policy) return null
  const premiumPayable = getPremiumPayable(policy)
  if (
    !isAmount(premiumPayable) ||
    !isAmount(policy.total_paid_amount_in_cents)
  ) {
    return null
  }
  return policy.total_paid_amount_in_cents - premiumPayable
}

const Payments = props => {
  const classes = useStyle()

  const [expanded, setExpanded] = useState(false)
  const [viewDocumentUrl, setViewDocumentUrl] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [paymentToReplay, setPaymentToReplay] = useState(null)
  const currencyCode = useProductCurrency(
    props.policy && props.policy.product_code
  )
  const partnerGuid = getPartnerGuid()
  const showBalances = partnerGuid === 'omk'
  const showTransferButton = partnerGuid === 'omk'

  const [transferPayment, setTransferPayment] = useState(null)
  const [transferDestPolicy, setTransferDestPolicy] = useState(null)
  const [transferSearchQ, setTransferSearchQ] = useState('')
  const [transferSuggestions, setTransferSuggestions] = useState([])
  const [transferSearchOpen, setTransferSearchOpen] = useState(false)
  const [transferAmount, setTransferAmount] = useState('')
  const [transferReason, setTransferReason] = useState('')
  const [transferLoading, setTransferLoading] = useState(false)
  const [transferError, setTransferError] = useState('')
  const [transferSuccess, setTransferSuccess] = useState(false)

  useEffect(
    function() {
      if (transferSearchQ.trim().length < 2) {
        setTransferSuggestions([])
        return
      }
      const t = setTimeout(function() {
        getElasticSearchResults({
          query: transferSearchQ.trim(),
          document_type: 'Policy',
          page: 0,
          size: 8
        })
          .then(function(res) {
            const items =
              (res &&
                res.data &&
                (res.data.results || res.data.hits || res.data.data)) ||
              []
            setTransferSuggestions(
              items.filter(function(p) {
                return p && p.guid && p.policy_number
              })
            )
          })
          .catch(function() {
            setTransferSuggestions([])
          })
      }, 250)
      return function() {
        clearTimeout(t)
      }
    },
    [transferSearchQ]
  )

  const openTransferDialog = payment => {
    setTransferPayment(payment)
    setTransferDestPolicy(null)
    setTransferSearchQ('')
    setTransferSuggestions([])
    setTransferSearchOpen(false)
    setTransferAmount('')
    setTransferReason('')
    setTransferError('')
    setTransferSuccess(false)
  }

  const closeTransferDialog = () => {
    setTransferPayment(null)
    setTransferLoading(false)
  }

  const handleTransferSubmit = async () => {
    if (!transferDestPolicy) {
      setTransferError('Select a destination policy.')
      return
    }
    const amountCents = Math.round(parseFloat(transferAmount) * 100)
    if (!transferAmount || isNaN(amountCents) || amountCents <= 0) {
      setTransferError('Enter a valid transfer amount.')
      return
    }
    if (amountCents > transferPayment.amount_in_cents) {
      setTransferError(
        `Amount cannot exceed ${(transferPayment.amount_in_cents / 100).toFixed(
          2
        )}.`
      )
      return
    }
    if (!transferReason.trim()) {
      setTransferError('A reason is required.')
      return
    }
    setTransferLoading(true)
    setTransferError('')
    try {
      const res = await initiatePremiumTransfer({
        source_payment_guid: transferPayment.guid,
        source_policy_guid: props.policy && props.policy.guid,
        transfer_amount_in_cents: amountCents,
        destinations: [
          { policy_guid: transferDestPolicy.guid, amount_in_cents: amountCents }
        ],
        reason: transferReason.trim()
      })
      setTransferSuccess(transferSubmittedMessage(res && res.data))
    } catch (e) {
      setTransferError(
        approvalErrorMessage(e, 'Transfer request failed. Please try again.')
      )
    } finally {
      setTransferLoading(false)
    }
  }

  const money = amountInCents =>
    `${currencyCode} ${(amountInCents / 100).toLocaleString()}`

  useEffect(() => {
    props.getAllPayments()
  }, [props])
  if (props.loader) {
    return <SkeletonWrapper loading={props.loader} />
  }

  if (!props.payments) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} className={classes.claimwrapper}>
          <Grid container spacing={2} className={classes.claimdetailswrapper}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className={classes.ClaimDetailscontent}
            >
              <p style={{ textAlign: 'center', marginTop: '30px' }}>
                No payments found
              </p>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const paymemtDisplay = expanded
    ? props.payments
    : props.payments
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 2)

  if (props.loader) {
    return <SkeletonWrapper loading={props.loader} />
  }

  // Position on the policy as a whole: what the premium is, what has been
  // received, what is still due today, and any surplus beyond the premium.
  const premiumPayable = getPremiumPayable(props.policy)
  const totalPaid = props.policy
    ? props.policy.total_paid_amount_in_cents
    : null
  const dueNow =
    props.policy && props.policy.granularity_balance_amount_in_cents > 0
      ? props.policy.granularity_balance_amount_in_cents
      : null
  const excessPaid = getExcessPaid(props.policy)
  const hasExcess = isAmount(excessPaid) && excessPaid > 0
  const showPosition =
    showBalances && (isAmount(premiumPayable) || isAmount(totalPaid))

  if (paymemtDisplay.length > 0) {
    return (
      <>
        {showPosition && (
          <Box
            sx={{
              mt: 3,
              mb: 2,
              p: 2,
              borderRadius: '8px',
              border: hasExcess ? '1px solid #4B7E34' : '1px solid #e2e8f0',
              background: hasExcess ? '#f2f8ef' : '#fff'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3} md={3}>
                <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
                  Total Premium Payable
                </p>
                <h6 style={{ margin: '4px 0 0', fontSize: '14px' }}>
                  {isAmount(premiumPayable) ? money(premiumPayable) : 'N/A'}
                </h6>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
                  Total Paid
                </p>
                <h6 style={{ margin: '4px 0 0', fontSize: '14px' }}>
                  {isAmount(totalPaid) ? money(totalPaid) : 'N/A'}
                </h6>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
                  Amount Due Now
                </p>
                <h6
                  style={{
                    margin: '4px 0 0',
                    fontSize: '14px',
                    color:
                      isAmount(dueNow) && dueNow > 0 ? '#c53030' : 'inherit'
                  }}
                >
                  {isAmount(dueNow) ? money(dueNow) : 'N/A'}
                </h6>
              </Grid>
              {hasExcess && (
                <Grid item xs={12} sm={3} md={3}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#4a5568' }}>
                    Excess / Overpayment Balance
                  </p>
                  <h6
                    style={{
                      margin: '4px 0 0',
                      fontSize: '16px',
                      color: '#4B7E34'
                    }}
                  >
                    {money(excessPaid)}
                  </h6>
                </Grid>
              )}
            </Grid>
            {hasExcess && (
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: '11px',
                  color: '#718096'
                }}
              >
                The customer has paid more than the premium payable. The excess
                is held on the policy and carried forward against future
                premiums.
              </p>
            )}
          </Box>
        )}
        {paymemtDisplay.map((payment, index) => (
          <>
            <Grid container className={classes.policyWrapper} key={index}>
              <Grid item xs={12} sm={12} md={12}>
                <Grid container className={classes.policyHeader}>
                  <Grid item xs={12} sm={6} md={6}>
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        md={2}
                        sm={2}
                        className={classes.policyIconWrapper}
                      >
                        <Box className={classes.policyIcons}>
                          <NoteAddOutlinedIcon
                            fontSize="14px"
                            sx={{ color: '#ffffff' }}
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={10}
                        sm={10}
                        className={classes.policyHeaderContent}
                      >
                        <h6>{payment.product_name || 'N/A'}</h6>
                        <p> Product Name</p>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Grid container>
                      <Grid item xs={12} md={6} sm={6}>
                        <div></div>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sm={6}
                        className={classes.policyActiveStatusWrapper}
                      >
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          gap={1}
                        >
                          {showTransferButton &&
                            (payment.processing_status === 'Succeeded' ||
                              payment.status === 'Succeeded') && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openTransferDialog(payment)}
                                style={{
                                  fontSize: '11px',
                                  textTransform: 'none'
                                }}
                              >
                                Transfer
                              </Button>
                            )}
                          <Box display="grid" justifyContent="flex-end">
                            <p> Processing Status</p>
                            <h6>{payment.processing_status || 'N/A'}</h6>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <div className={classes.policyHeaderBorder}></div>
                </Grid>

                <Grid
                  container
                  spacing={2}
                  className={classes.policyContentWrapper}
                >
                  <Grid item xs={12} sm={3} md={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3} md={3}></Grid>
                      <Grid item xs={12} sm={9} md={9}>
                        <p>Payment Reference </p>
                        <h6> {payment.mno_reference || 'N/A'}</h6>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <p> Created At </p>
                    <h6>{formatDateLocale(payment.created_at) || 'N/A'}</h6>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <p>Effected At </p>
                    <h6>{formatDateLocale(payment.effected_at) || 'N/A'}</h6>
                  </Grid>
                  <Grid item xs={12} sm={3} md={3}>
                    <p>Proof Of Payment </p>
                    <h6>
                      {payment.proof_of_payment_url ? (
                        <>
                          <Tooltip title="View Document">
                            <span
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                setViewDocumentUrl(payment.proof_of_payment_url)
                              }
                            >
                              <VisibilityIcon sx={{ color: ' #4B7E34' }} />
                            </span>
                          </Tooltip>
                          <Tooltip title="Download Document">
                            <span
                              style={{ cursor: 'pointer', marginLeft: '10px' }}
                            >
                              <a
                                href={payment.proof_of_payment_url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FileDownloadIcon sx={{ color: ' #000' }} />
                              </a>{' '}
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        'N/A'
                      )}
                    </h6>
                  </Grid>
                </Grid>
                <div className={classes.policyContentBorder}></div>
                <Grid
                  container
                  spacing={2}
                  className={classes.policyFooterWrapper}
                >
                  <Grid item xs={12} md={6} sm={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6} sm={6}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3} md={3}></Grid>
                          <Grid item xs={12} sm={9} md={9}>
                            <p>Payment Failure Reason</p>
                            <h6>{payment.payment_failure_reason || 'N/A'}</h6>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={6} sm={6}>
                        <p> Message</p>
                        <h6>{payment.message || 'N/A'}</h6>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6} sm={6}>
                    <div className={classes.policyBalance}>
                      <p>Amount</p>
                      <h6>
                        {`${currencyCode} ${payment.amount_in_cents / 100}` ||
                          '0.00'}{' '}
                      </h6>
                      {payment.processing_status === 'Failed' &&
                        canAccess(['PREMIUM_UPDATE']) && (
                          <Button
                            variant="outlined"
                            size="small"
                            style={{
                              marginTop: '10px',
                              borderColor: '#FBA92D',
                              color: '#000000',
                              borderRadius: '8px',
                              textTransform: 'capitalize'
                            }}
                            onClick={() => setPaymentToReplay(payment)}
                          >
                            Replay Payment
                          </Button>
                        )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        ))}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            {paymemtDisplay.length > 1 && (
              <Button
                style={{
                  background: '#FBA92D',
                  borderRadius: '8px',
                  fontFamily: 'Open Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '15px',
                  boxShadow: 'none',
                  padding: '10px',
                  marginTop: '20px',
                  textTransform: 'capitalize'
                }}
                variant="contained"
                size="large"
                endIcon={expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? `Show Less` : `Show More`}
              </Button>
            )}
          </Grid>
        </Grid>
        <ViewDocument
          title="Proof Of Payment Document"
          open={viewDocumentUrl}
          close={() => setViewDocumentUrl(null)}
          viewDocumentUrl={viewDocumentUrl}
          numPages={numPages}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        />

        <ReplayPayment
          open={!!paymentToReplay}
          payment={paymentToReplay}
          onClose={() => setPaymentToReplay(null)}
          onSuccess={() => {
            // The API publishes the payment for allocation asynchronously, so
            // give it a moment before reloading the list.
            setTimeout(() => props.getAllPayments(), 2000)
          }}
        />

        {transferPayment && (
          <Dialog open onClose={closeTransferDialog} fullWidth>
            <DialogTitle>Transfer premium</DialogTitle>
            <DialogContent>
              {transferSuccess ? (
                <p style={{ color: 'green' }}>{transferSuccess}</p>
              ) : (
                <>
                  {transferError && (
                    <p style={{ color: 'red', marginBottom: '8px' }}>
                      {transferError}
                    </p>
                  )}
                  <p style={{ fontSize: '13px', marginBottom: '12px' }}>
                    Transfer <strong>{transferPayment.mno_reference}</strong> (
                    {(transferPayment.amount_in_cents / 100).toFixed(2)}) to
                    another policy. This creates a pending approval record.
                  </p>
                  <div
                    className="form-group"
                    style={{ marginBottom: '12px', position: 'relative' }}
                  >
                    <label style={{ fontSize: '13px' }}>
                      Destination policy
                    </label>
                    {transferDestPolicy ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          fontSize: '13px'
                        }}
                      >
                        <span>
                          <strong>{transferDestPolicy.policy_number}</strong>
                          {transferDestPolicy.full_name
                            ? ` — ${transferDestPolicy.full_name}`
                            : ''}
                        </span>
                        <button
                          type="button"
                          onClick={function() {
                            setTransferDestPolicy(null)
                            setTransferSearchQ('')
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '11px',
                            opacity: 0.6
                          }}
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="text"
                          className="form-control"
                          value={transferSearchQ}
                          onChange={function(e) {
                            setTransferSearchQ(e.target.value)
                            setTransferSearchOpen(true)
                          }}
                          onFocus={function() {
                            setTransferSearchOpen(true)
                          }}
                          placeholder="Search by policy number…"
                          autoComplete="off"
                        />
                        {transferSearchOpen &&
                          transferSearchQ.trim().length >= 2 && (
                            <div
                              style={{
                                position: 'absolute',
                                zIndex: 999,
                                left: 0,
                                right: 0,
                                background: '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                maxHeight: '200px',
                                overflowY: 'auto'
                              }}
                            >
                              {transferSuggestions.length === 0 ? (
                                <div
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    opacity: 0.6
                                  }}
                                >
                                  No policies found
                                </div>
                              ) : (
                                transferSuggestions.map(function(p) {
                                  return (
                                    <div
                                      key={p.guid}
                                      onMouseDown={function(e) {
                                        e.preventDefault()
                                        setTransferDestPolicy(p)
                                        setTransferSearchOpen(false)
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        borderBottom: '1px solid #f0f0f0'
                                      }}
                                      onMouseEnter={function(e) {
                                        e.currentTarget.style.background =
                                          '#f5f5f5'
                                      }}
                                      onMouseLeave={function(e) {
                                        e.currentTarget.style.background = ''
                                      }}
                                    >
                                      <strong>{p.policy_number}</strong>
                                      {p.full_name ? (
                                        <span style={{ opacity: 0.6 }}>
                                          {' '}
                                          — {p.full_name}
                                        </span>
                                      ) : null}
                                    </div>
                                  )
                                })
                              )}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '13px' }}>
                      Amount to transfer (max{' '}
                      {(transferPayment.amount_in_cents / 100).toFixed(2)})
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={transferAmount}
                      onChange={e => setTransferAmount(e.target.value)}
                      min={0.01}
                      max={transferPayment.amount_in_cents / 100}
                      step={0.01}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Reason</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={transferReason}
                      onChange={e => setTransferReason(e.target.value)}
                      placeholder="e.g. Wrong policy number, overpayment transfer…"
                    />
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {!transferSuccess && (
                <Button
                  onClick={handleTransferSubmit}
                  variant="contained"
                  disabled={transferLoading}
                  style={{ backgroundColor: '#FBA92D', color: 'white' }}
                >
                  {transferLoading ? 'Submitting…' : 'Submit for approval'}
                </Button>
              )}
              <Button onClick={closeTransferDialog} variant="contained">
                {transferSuccess ? 'Close' : 'Cancel'}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} className={classes.claimwrapper}>
        <Grid container spacing={2} className={classes.claimdetailswrapper}>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            className={classes.ClaimDetailscontent}
          >
            <p style={{ textAlign: 'center', marginTop: '30px' }}>
              No payments found
            </p>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Payments
